import { useCallback, useEffect, useState } from "react";
import type { RoleAuditRecord } from "@hash/shared";
import { api } from "../api/client";

/**
 * Read-only, reverse-chronological role-change log (Story 12.4).
 * Admin oversight of misman appointments without an approval bottleneck:
 * every grant/revoke of misman or admin lands here, immutably.
 */
export function RoleAuditPage() {
  const [records, setRecords] = useState<RoleAuditRecord[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (nextCursor: string | undefined) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.admin.roleAudit(nextCursor);
      setRecords((prev) =>
        nextCursor ? [...prev, ...res.records] : res.records
      );
      setCursor(res.cursor);
      setHasMore(Boolean(res.cursor));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load role changes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(undefined);
  }, [load]);

  return (
    <div className="min-h-screen pb-24">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3">
        <h2 className="text-xl font-bold text-slate-900">Role changes</h2>
        <p className="mt-1 text-sm text-slate-500">
          Every misman and admin grant or revoke, newest first.
        </p>
      </header>

      {error && <p className="px-4 py-3 text-sm text-red-600">{error}</p>}

      {records.length === 0 && !loading ? (
        <p className="px-4 py-6 text-sm text-slate-500">No role changes yet.</p>
      ) : (
        <ul className="divide-y divide-slate-200 bg-white">
          {records.map((r) => (
            <li key={r.id} className="px-4 py-3">
              <div className="text-slate-900">
                <span className="font-medium">{r.actorName}</span>{" "}
                {r.action === "grant" ? "made" : "removed"}{" "}
                <span className="font-medium">{r.targetName}</span>{" "}
                {r.role === "admin" ? (
                  <>{r.action === "grant" ? "an admin" : "as admin"}</>
                ) : (
                  <>
                    {r.action === "grant" ? "misman of" : "as misman of"}{" "}
                    <span className="font-medium">{r.kennelName}</span>
                  </>
                )}
              </div>
              <div className="text-sm text-slate-500">
                {new Date(r.at).toLocaleString()}
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="px-4 py-4">
        {hasMore ? (
          <button
            onClick={() => load(cursor)}
            disabled={loading}
            className="w-full h-12 rounded-lg bg-slate-900 text-white font-medium active:bg-slate-700 disabled:opacity-50"
          >
            {loading ? "Loading…" : "Load more"}
          </button>
        ) : loading ? (
          <p className="text-center text-sm text-slate-500">Loading…</p>
        ) : null}
      </div>
    </div>
  );
}
